// Local-storage queue for "user clicked an interactive button while
// signed-out, and we need to replay that intent after they sign up."
// (PR2 / PUBLIC-DECKS plan, §5.3 + §6.)
//
// Single-slot queue keyed under `mtg-oracle:queued-action`. We only
// keep the most recent intent — if the user clicks Ask, then Playtest
// before signing up, we replay Playtest. Multi-slot queueing would
// require a UI to disambiguate after sign-in and the simpler path is
// fine for v1.
//
// TTL: 10 minutes. Anti-staleness — someone signs up, gets distracted
// for an hour, comes back — we don't want to silently fire a stale
// action they may have forgotten about. Past TTL the entry is silently
// dropped on read.

export type QueuedActionKind = "ask" | "playtest" | "build-similar" | "fork";

export interface QueuedAction {
  /** Action kind. The replayer dispatches per-kind. */
  action: QueuedActionKind;
  /** Free-form payload — schema depends on action kind. The
   *  replayer validates the shape before dispatching. */
  payload: Record<string, unknown>;
  /** The deck this action targets. The replayer rejects mismatches
   *  so a queued action on /decks/a/foo never fires on /decks/b/bar. */
  deckId: string;
  /** Owner username of the deck — keeps the (owner, slug) pair
   *  together so the page can verify both segments match. */
  owner: string;
  /** Epoch ms when the user clicked. Past TTL_MS the entry is
   *  silently discarded on read. */
  ts: number;
}

const STORAGE_KEY = "mtg-oracle:queued-action";
const TTL_MS = 10 * 60 * 1000;
const ALLOWED_ACTIONS: ReadonlyArray<QueuedActionKind> = [
  "ask",
  "playtest",
  "build-similar",
  "fork",
];

/**
 * Persist a click intent. Overwrites any prior queued action — see
 * the file header for why single-slot is OK.
 */
export function queueAction(input: Omit<QueuedAction, "ts">): void {
  if (typeof window === "undefined") return;
  const entry: QueuedAction = { ...input, ts: Date.now() };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Quota / private-mode failure — silently drop. The user can
    // re-click after sign-up; worst case they retype the question.
  }
}

/**
 * Read and validate the queued action without removing it. Returns
 * null if the slot is empty, the JSON is malformed, the schema is
 * unrecognized, the action type is outside the allow-list, or the
 * entry has aged past TTL_MS.
 */
export function peekQueuedAction(): QueuedAction | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Corrupt slot — clear so next read isn't blocked by garbage.
    clearQueuedAction();
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const obj = parsed as Partial<QueuedAction>;
  if (
    typeof obj.action !== "string" ||
    !ALLOWED_ACTIONS.includes(obj.action as QueuedActionKind) ||
    typeof obj.deckId !== "string" ||
    typeof obj.owner !== "string" ||
    typeof obj.ts !== "number" ||
    !obj.payload ||
    typeof obj.payload !== "object"
  ) {
    return null;
  }
  if (Date.now() - obj.ts > TTL_MS) return null;
  return obj as QueuedAction;
}

/** Remove the queued action from storage. Idempotent. */
export function clearQueuedAction(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode or corrupted storage — not actionable.
  }
}
